import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera, Construction } from "lucide-react";
import type { PProcessAttachmentsDto } from "@hisense/shared";
import { apiFetch, getLocalizedError } from "@/lib/api";
import { useMenuAccess } from "@/app/access";
import { FileDropList, savedToDraft, type DraftFile } from "@/components/docs/FileDropList";
import { PreviewGate } from "@/components/shell/FeaturePreviewGate";
import { LoadingState } from "@/components/ui/States";

/**
 * Ảnh trên Quy trình P (Roary 04/11/2026): ảnh thường chụp từ WeChat — sao chép rồi Ctrl+V vào ô, kéo thả hoặc chọn tệp. Gắn theo PI
 * và nấc (`step`), hoặc ô `general`. Nấc "Khách đặt hàng" không có ô riêng mà hiện bằng chứng đặt hàng đã lưu trên PI.
 * Danh sách TỰ LƯU sau mỗi lần thêm / gỡ (màn theo dõi không có nút Lưu). Còn khoá mật khẩu "Đang làm" (PREVIEW_FEATURES.pProcessPhotos).
 */
const queryKey = (piId: string) => ["p-process-attachments", piId];

/**
 * Nút "Ảnh" ở mỗi nấc — mang dấu "Đang làm" (biểu tượng rào chắn) tới khi khách nghiệm thu. Chữ "Đang làm" chỉ hiện ở nút của ô ảnh
 * chung (`label`), không lặp lại 13 lần trên mỗi nấc.
 */
export function PhotoToggle({ count, open, onClick, label = false }: { count: number; open: boolean; onClick: () => void; label?: boolean }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-2xs ${open ? "border-brand bg-brand-tint text-ink" : "border-line text-ink-soft hover:border-line-strong hover:text-ink"}`}
    >
      <Camera aria-hidden="true" className="size-3" />
      {count > 0 ? t("pProcess.photos.count", { count }) : t("pProcess.photos.button")}
      <span className="flex items-center gap-0.5 text-info" title={t("pProcess.photos.inProgress")}>
        <Construction aria-hidden="true" className="size-3" />
        {label ? t("pProcess.photos.inProgress") : <span className="sr-only">{t("pProcess.photos.inProgress")}</span>}
      </span>
    </button>
  );
}

/** Ô ảnh của một nấc (hoặc `general`) — nằm sau cổng mật khẩu, chỉ gọi API khi đã mở khoá. */
export function PhotoSlot({ piId, step }: { piId: string; step: string }) {
  return (
    <div className="mt-2 w-full">
      <PreviewGate feature="pProcessPhotos" inline>
        <SlotBody piId={piId} step={step} />
      </PreviewGate>
    </div>
  );
}

export function usePProcessPhotos(piId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKey(piId),
    enabled,
    queryFn: ({ signal }) => apiFetch<{ data: PProcessAttachmentsDto }>("/p-process/attachments", { query: { piId }, signal }).then((r) => r.data),
  });
}

function SlotBody({ piId, step }: { piId: string; step: string }) {
  const { t } = useTranslation();
  const canEdit = useMenuAccess("pProcess")("update");
  const client = useQueryClient();
  const query = usePProcessPhotos(piId, true);
  const [files, setFiles] = useState<DraftFile[] | null>(null);
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  // Lưu nối đuôi: tải nhiều ảnh một lúc gọi onChange nhiều lần — lần sau phải chờ lần trước, gửi danh sách MỚI NHẤT.
  const chain = useRef<Promise<void>>(Promise.resolve());
  const latest = useRef<DraftFile[]>([]);

  useEffect(() => {
    if (query.data && files === null) setFiles(query.data.attachments.filter((a) => a.step === step).map(savedToDraft));
  }, [query.data, files, step]);

  if (step === "piIssued") return <OrderEvidence piId={piId} data={query.data} loading={query.isLoading} />;
  if (query.isLoading || files === null) return query.error ? <p role="alert" className="text-xs text-danger">{getLocalizedError(query.error, t, "pProcess.photos.error")}</p> : <LoadingState />;

  const save = (next: DraftFile[]) => {
    latest.current = next;
    setFiles(next);
    setStatus("saving");
    chain.current = chain.current.then(async () => {
      if (latest.current !== next) return; // đã có danh sách mới hơn, lần lưu sau sẽ gửi nó
      try {
        const res = await apiFetch<{ data: PProcessAttachmentsDto }>("/p-process/attachments", {
          method: "PUT",
          body: {
            piId, step,
            attachments: next.map((f) => f.id
              ? { id: f.id, kind: f.kind, fileName: f.fileName, fileSize: f.fileSize, mimeType: f.mimeType }
              : { key: f.key, kind: f.kind, fileName: f.fileName, fileSize: f.fileSize, mimeType: f.mimeType }),
          },
        });
        client.setQueryData(queryKey(piId), res.data);
        if (latest.current === next) setFiles(res.data.attachments.filter((a) => a.step === step).map(savedToDraft));
        setStatus("saved");
        setError(null);
        await client.invalidateQueries({ queryKey: ["p-process"] });
      } catch (e) {
        setStatus("error");
        setError(getLocalizedError(e, t, "pProcess.photos.error"));
      }
    });
  };

  return (
    <div className="space-y-1.5">
      {step === "general" && <p className="text-xs text-ink-soft">{t("pProcess.photos.generalHint")}</p>}
      {canEdit ? <p className="text-2xs text-ink-faint">{t("pProcess.photos.hint")}</p> : <p className="text-2xs text-ink-faint">{t("pProcess.photos.readOnly")}</p>}
      <FileDropList files={files} onChange={canEdit ? save : undefined} readOnly={!canEdit} folder="p-process" compact />
      {status === "saving" && <p className="text-2xs text-ink-faint" role="status">{t("pProcess.photos.saving")}</p>}
      {status === "saved" && <p className="text-2xs text-ok" role="status">{t("pProcess.photos.saved")}</p>}
      {error && <p role="alert" className="text-xs text-danger">{error}</p>}
    </div>
  );
}

function OrderEvidence({ piId, data, loading }: { piId: string; data: PProcessAttachmentsDto | undefined; loading: boolean }) {
  const { t } = useTranslation();
  if (loading || !data) return <LoadingState />;
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium text-ink">{t("pProcess.photos.orderEvidence")}</p>
      {data.orderEvidence === null ? (
        <p className="text-xs text-ink-soft">{t("pProcess.photos.orderEvidenceHidden")}</p>
      ) : data.orderEvidence.length === 0 ? (
        <p className="text-xs text-ink-soft">{t("pProcess.photos.orderEvidenceNone")}</p>
      ) : (
        <FileDropList files={data.orderEvidence.map(savedToDraft)} folder="pi-attachments" readOnly compact />
      )}
      {data.orderEvidence !== null && <Link to={`/proforma-invoices/${piId}`} className="text-xs text-brand underline">{t("pProcess.photos.openPi")}</Link>}
    </div>
  );
}
